<template>
  <Teleport to="body">
    <div
      v-if="active"
      ref="rootEl"
      class="fixed flex flex-col rounded-lg border border-white/10 bg-[var(--surface-color,#0d0e12)] shadow-2xl overflow-hidden"
      :style="frameStyle"
    >
      <!-- Drag handle / header -->
      <div
        ref="headerEl"
        class="flex items-center gap-2 px-3 h-10 border-b border-white/5 select-none cursor-grab active:cursor-grabbing bg-black/40"
      >
        <span class="flex items-center gap-1.5 text-[11px] font-semibold">
          <span
            class="w-2 h-2 rounded-full"
            :class="active.status === 'active' ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'"
          ></span>
          <span class="text-white">{{ statusLabel }}</span>
          <span v-if="callDuration" class="text-text-dim tabular-nums">· {{ callDuration }}</span>
        </span>
        <span class="flex-1"></span>
        <button
          type="button"
          class="p-1 rounded hover:bg-white/10 text-text-muted hover:text-white"
          :title="$t('chat.call.hangup')"
          :aria-label="$t('chat.call.hangup')"
          data-no-drag
          @click="hangup"
        >
          <el-icon size="12"><Close /></el-icon>
        </button>
      </div>

      <!-- Video grid -->
      <div
        class="flex-1 min-h-0 grid gap-1 p-1 bg-black"
        :class="gridClass"
      >
        <!-- Local tile -->
        <ChatCallTile
          :stream="active.localStream"
          :muted="true"
          :is-self="true"
          :label="$t('chat.call.you')"
          :audio-off="active.muted"
          :video-off="active.cameraOff || active.kind !== 'video'"
        />
        <!-- Remote peer tiles -->
        <ChatCallTile
          v-for="remote in remoteList"
          :key="remote.userId"
          :stream="remote.videoStream || remote.audioStream"
          :label="remote.userId"
          :audio-off="!remote.hasAudio"
          :video-off="!remote.hasVideo"
        />
        <!-- Screen share takes a dedicated tile per presenter so layout doesn't collapse -->
        <ChatCallTile
          v-for="remote in screensharingList"
          :key="'screen-' + remote.userId"
          :stream="remote.screenStream"
          :label="'🖥 ' + remote.userId"
          :video-off="false"
          :audio-off="true"
        />
      </div>

      <!-- Controls -->
      <div class="flex items-center justify-center gap-2 p-2 border-t border-white/5 bg-black/40">
        <button
          type="button"
          class="p-2.5 rounded-full transition-colors"
          :class="active.muted ? 'bg-red-500/80 text-white' : 'bg-white/10 text-white hover:bg-white/20'"
          :title="active.muted ? $t('chat.call.unmute') : $t('chat.call.mute')"
          @click="toggleMute"
        >
          <el-icon size="14"><Microphone v-if="!active.muted" /><Mute v-else /></el-icon>
        </button>
        <button
          v-if="active.kind === 'video'"
          type="button"
          class="p-2.5 rounded-full transition-colors"
          :class="active.cameraOff ? 'bg-red-500/80 text-white' : 'bg-white/10 text-white hover:bg-white/20'"
          :title="active.cameraOff ? $t('chat.call.camera_on') : $t('chat.call.camera_off')"
          @click="toggleCamera"
        >
          <el-icon size="14"><VideoCamera v-if="!active.cameraOff" /><VideoPause v-else /></el-icon>
        </button>
        <button
          type="button"
          class="p-2.5 rounded-full transition-colors"
          :class="active.screenSharing ? 'bg-primary text-black' : 'bg-white/10 text-white hover:bg-white/20'"
          :title="active.screenSharing ? $t('chat.call.stop_share') : $t('chat.call.share_screen')"
          @click="onToggleScreen"
        >
          <el-icon size="14"><Monitor /></el-icon>
        </button>
        <button
          type="button"
          class="p-2.5 rounded-full bg-red-500 text-white hover:bg-red-500/90 transition-colors"
          :title="$t('chat.call.hangup')"
          @click="hangup"
        >
          <el-icon size="14"><PhoneFilled class="rotate-[135deg]" /></el-icon>
        </button>
      </div>

      <!-- Resize grip -->
      <div
        ref="gripEl"
        class="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
        data-no-drag
      >
        <svg viewBox="0 0 10 10" class="w-full h-full text-white/30">
          <path d="M 0 10 L 10 0 M 4 10 L 10 4 M 8 10 L 10 8" stroke="currentColor" stroke-width="1" fill="none" />
        </svg>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { Close, Microphone, Mute, VideoCamera, VideoPause, Monitor, PhoneFilled } from '@element-plus/icons-vue'
import { useCall } from '~/composables/chat/useCall'
import { useDragResize } from '~/composables/useDragResize'

/**
 * Floating call window. Builds on the same `useDragResize` primitive as
 * DetachedChatWindow. Hosts the video grid + per-track controls.
 *
 * Layout:
 *   - Header: status dot + timer + close button (drag handle)
 *   - Body: responsive grid of video tiles (1→2→3→4+)
 *   - Footer: mic / camera / screen-share / hangup buttons
 */

const { active, toggleMute, toggleCamera, toggleScreenShare, hangup } = useCall()

const headerEl = ref(null)
const gripEl = ref(null)
const rootEl = ref(null)

// Initial position: right edge, vertically centred-ish. Survives as long
// as the call is active; each call is a fresh window.
const initial = (() => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720
  return {
    x: Math.max(40, vw - 540),
    y: Math.max(40, (vh - 380) / 2),
    width: 480,
    height: 360
  }
})()

const { x, y, width, height } = useDragResize({
  headerEl,
  gripEl,
  initial,
  min: { width: 320, height: 260 }
})

const frameStyle = computed(() => ({
  left: `${x.value}px`,
  top: `${y.value}px`,
  width: `${width.value}px`,
  height: `${height.value}px`,
  zIndex: 95  // above detached chat windows (z 50+), below modals (100)
}))

// Grid columns adapt to total tile count. Local (+N remotes) + screen share
// tiles. Simple heuristic: sqrt(count).
const totalTiles = computed(() => {
  if (!active.value) return 1
  const screens = [...active.value.remotes.values()].filter(r => r.hasScreen).length
  return 1 + active.value.remotes.size + screens
})

const gridClass = computed(() => {
  const n = totalTiles.value
  if (n <= 1) return 'grid-cols-1 grid-rows-1'
  if (n === 2) return 'grid-cols-2 grid-rows-1'
  if (n <= 4) return 'grid-cols-2 grid-rows-2'
  if (n <= 6) return 'grid-cols-3 grid-rows-2'
  return 'grid-cols-3 grid-rows-3'
})

const remoteList = computed(() => {
  if (!active.value) return []
  return [...active.value.remotes.values()]
})

const screensharingList = computed(() =>
  remoteList.value.filter(r => r.hasScreen)
)

const statusLabel = computed(() => {
  if (!active.value) return ''
  if (active.value.status === 'outgoing') return 'Ringing…'
  if (active.value.status === 'connecting') return 'Connecting…'
  if (active.value.status === 'active') return 'Live'
  return active.value.status
})

/* Call timer */
const now = ref(Date.now())
let tick = null
onMounted(() => { tick = setInterval(() => { now.value = Date.now() }, 1000) })
onBeforeUnmount(() => { if (tick) clearInterval(tick) })

const callDuration = computed(() => {
  if (!active.value || active.value.status !== 'active') return ''
  const sec = Math.max(0, Math.floor((now.value - active.value.startedAt) / 1000))
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
})

async function onToggleScreen() {
  try {
    await toggleScreenShare()
  } catch (err) {
    // getDisplayMedia may reject if the user cancels the picker
    // eslint-disable-next-line no-console
    console.warn('[chat:call] screen share toggle aborted', err)
  }
}
</script>
